import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Sun, Moon, Robot } from '@phosphor-icons/react';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [kicked, setKicked] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login, oneClickLogin } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const oneClickToken = searchParams.get('token');

  // Show kicked message once on load
  useEffect(() => {
    if (searchParams.get('kicked') === '1') {
      setKicked(true);
      // Clean URL
      window.history.replaceState({}, '', '/login');
    }
  }, [searchParams]);

  const getFirstPage = (userData) => {
    if (userData?.role === 'super_admin') return '/app/dashboard';
    const perms = userData?.permissions || [];
    if (perms.includes('dashboard')) return '/app/dashboard';
    if (perms.includes('live_chat')) return '/app/bot-live-chat';
    if (perms.includes('whatsapp_chat')) return '/app/whatsapp-chat';
    if (perms.includes('broadcast')) return '/app/broadcast';
    if (perms.includes('admin_chat')) return '/app/chat';
    if (perms.includes('credits')) return '/app/credits';
    if (perms.includes('create_users')) return '/app/staff';
    return '/app/bot-live-chat';
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const data = await login(email, password);
      navigate(getFirstPage(data.user));
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleOneClick = async () => {
    setError('');
    setLoading(true);
    try {
      const data = await oneClickLogin(oneClickToken);
      navigate(getFirstPage(data.user));
    } catch (err) {
      setError('One-click login failed or expired');
    } finally {
      setLoading(false);
    }
  };

  if (oneClickToken) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <div className="w-full max-w-sm border border-border rounded-md p-8 text-center space-y-4">
          <img src="/logo.png" alt="Telegram Bot" className="w-14 h-14 object-contain mx-auto" />
          <h2 className="text-2xl font-semibold tracking-tight" style={{ fontFamily: 'Cabinet Grotesk' }}>One-Click Login</h2>
          {error && <p className="text-sm text-destructive">{error}</p>}
          <Button data-testid="one-click-login-btn" onClick={handleOneClick} disabled={loading} className="w-full">
            {loading ? 'Logging in...' : 'Login as Admin'}
          </Button>
        </div>
      </div>
    );
  }

  const bgImage = theme === 'dark'
    ? '/images/login_dark.png'
    : '/images/login_light.png';

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      <div className="hidden lg:flex lg:w-1/2 relative items-center justify-center overflow-hidden">
        <img src={bgImage} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="relative z-10 text-white p-12 max-w-lg">
          <img src="/logo.png" alt="Telegram Bot" className="w-16 h-16 object-contain mb-6" />
          <h1 className="text-4xl font-bold tracking-tight mb-3" style={{ fontFamily: 'Cabinet Grotesk' }}>
            Telegram Bot Manager
          </h1>
          <p className="text-lg opacity-80">Manage your Telegram bots, broadcasts, and users from one central dashboard.</p>
        </div>
      </div>

      <div className="w-full lg:w-1/2 flex items-center justify-center p-6 bg-background relative">
        <button
          data-testid="login-theme-toggle"
          onClick={toggleTheme}
          className="absolute top-4 right-4 p-2 rounded-md border border-border hover:bg-accent transition-colors"
        >
          {theme === 'light' ? <Moon size={20} /> : <Sun size={20} />}
        </button>

        <div className="w-full max-w-sm space-y-6">
          <div className="lg:hidden flex items-center gap-2 mb-4">
            <img src="/logo.png" alt="Telegram Bot" className="w-8 h-8 object-contain" />
            <span className="text-xl font-bold tracking-tight" style={{ fontFamily: 'Cabinet Grotesk' }}>TG Bot Admin</span>
          </div>

          <div>
            <h2 className="text-3xl font-bold tracking-tight" style={{ fontFamily: 'Cabinet Grotesk' }}>Sign In</h2>
            <p className="text-sm text-muted-foreground mt-1">Enter your credentials to access the dashboard</p>
          </div>

          <form onSubmit={handleLogin} className="space-y-4">
            {kicked && (
              <div className="p-3 rounded-md bg-amber-500/10 text-amber-700 dark:text-amber-400 text-sm border border-amber-500/20" data-testid="kicked-alert">
                You were logged out because your account was signed in from another device.
              </div>
            )}
            {error && (
              <div className="p-3 rounded-md bg-destructive/10 text-destructive text-sm border border-destructive/20" data-testid="login-error">
                {error}
              </div>
            )}

            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                data-testid="login-email-input"
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder=""
                required
                autoFocus
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                data-testid="login-password-input"
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder=""
                required
              />
            </div>

            <Button
              data-testid="login-submit-btn"
              type="submit"
              className="w-full active:scale-[0.98] transition-all duration-200"
              disabled={loading}
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
